"use client";

import { useState } from "react";
import { ArrowUpRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const manifestoItems = [{
  question: "What is your termite inspection process?",
  answer: "Our certified inspectors conduct a thorough examination of your property's foundation, crawl spaces, exterior perimeter, attic, and any other potential areas of concern. We identify active colonies and potential entry points, providing a detailed report and a customized treatment plan to protect your home.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_inspection_process.png",
  imageAlt: "Technician inspecting home foundation"
}, {
  question: "How do you treat active termite infestations?",
  answer: "We utilize targeted liquid treatments and advanced baiting systems that eliminate the colony at the source. Our methods are designed to be highly effective while ensuring the safety of your family and pets in the Punta Gorda area.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_treatment_active.webp",
  imageAlt: "Professional termite treatment application"
}, {
  question: "What documentation do I receive after treatment?",
  answer: "You will receive a comprehensive service report detailing the areas treated, the products used, and our recommendations for ongoing prevention. This documentation is essential for your records and future property transactions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_documentation.webp",
  imageAlt: "Detailed pest control service report"
}, {
  question: "How do you schedule termite services?",
  answer: "We coordinate with your schedule to provide convenient service windows. Our team confirms your appointment in advance and arrives on time, fully equipped to handle your termite protection needs efficiently.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_scheduling.png",
  imageAlt: "Technician arriving for scheduled service"
}, {
  question: "What is your protocol for long-term protection?",
  answer: "We offer ongoing monitoring and preventative maintenance programs to ensure your home remains termite-free. Regular inspections and proactive treatments are the best defense against future infestations.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_long_term.png",
  imageAlt: "Preventative termite monitoring station"
}, {
  question: "How do you handle environmental factors?",
  answer: "Our team monitors local weather patterns and environmental conditions in Southwest Florida to ensure our treatments remain effective year-round. We adjust our strategies to combat humidity and seasonal pest activity.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_environment.png",
  imageAlt: "Southwest Florida landscape"
}, {
  question: "What insurance coverage is included?",
  answer: "Peace River Pest Control is fully insured and licensed by the FDACS. We stand behind our work with professional-grade guarantees, ensuring your peace of mind and the protection of your most valuable asset.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_insurance.webp",
  imageAlt: "Professional pest control license and insurance"
}, {
  question: "How do I prepare for a termite treatment?",
  answer: "We provide clear instructions on how to prepare your home before our arrival. Generally, this involves clearing access to exterior walls and ensuring our technicians can reach all necessary areas of your property.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1659158/img/termite_prep.png",
  imageAlt: "Homeowner preparing for service"
}];
export default function () {
  const [openValue, setOpenValue] = useState<string>("item-0");
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="1664" id="termitepreventionfaq" className="relative bg-background text-foreground py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-16 lg:px-22">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5 relative">
            <div ref={headerRef} className={`lg:sticky lg:top-28 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              {}
              <div aria-hidden="true" className="pointer-events-none absolute -top-10 -left-4 lg:-left-8 select-none font-serif font-light leading-none text-foreground/5" style={{
              fontSize: "20vw"
            }}>
                05
              </div>

              {}
              <div className="relative flex items-center gap-3 mb-8">
                <span className="font-mono text-xs uppercase tracking-[0.18em] text-primary">
                  § 05 / FAQ — TERMITE PREVENTION
                </span>
              </div>

              {}
              <div className="relative flex items-center mb-10">
                <div className="h-px w-20 bg-primary" />
                <div className="h-1 w-1 bg-primary" />
              </div>

              {}
              <h2 className="relative font-default font-bold leading-[1.0] tracking-tight text-foreground mb-8" style={{
              fontSize: "clamp(2.25rem, 5.4vw, 4.75rem)"
            }}>
                Termite Prevention{" "}
                <span className="italic text-primary">Answers</span>
              </h2>

              {}
              <p className="relative font-default text-foreground/85 leading-relaxed mb-10 max-w-md" style={{
              fontSize: "clamp(1.0625rem, 1.2vw, 1.25rem)"
            }}>
                Expert protocols for protecting your home in Punta Gorda.
              </p>

              {}
              <Link to="/ContactPage" className="relative inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-primary group">
                <span className="relative">
                  Schedule Inspection
                  <span className="absolute left-0 -bottom-1 h-px bg-primary transition-all duration-300 w-[60%] group-hover:w-full" />
                </span>
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible value={openValue} onValueChange={v => setOpenValue(v)} className="w-full">
              {manifestoItems.map((item, i) => {
              const itemValue = `item-${i}`;
              const isOpen = openValue === itemValue;
              const indexLabel = `${String(i + 1).padStart(2, "0")} / ${String(manifestoItems.length).padStart(2, "0")}`;
              return <AccordionItem key={i} data-index={i} value={itemValue} className="relative border-b-0 border-t border-foreground/20">
                    {}
                    <span aria-hidden="true" className={`pointer-events-none absolute left-0 top-0 w-[2px] bg-primary transition-all duration-300 ease-out ${isOpen ? "h-full opacity-100" : "h-0 opacity-0"}`} />

                    <AccordionTrigger className={`group flex items-center justify-between gap-6 py-6 hover:no-underline rounded-md transition-colors duration-300 [&>svg]:hidden ${isOpen ? "text-foreground" : "text-foreground/85"}`}>
                      <span className="flex-1 text-left font-default font-bold leading-tight tracking-tight" style={{
                    fontSize: "clamp(1.125rem, 1.8vw, 1.5rem)"
                  }}>
                        {item.question}
                      </span>
                      <span className="hidden sm:block font-mono text-xs uppercase tracking-[0.18em] text-foreground/55 shrink-0">
                        {indexLabel}
                      </span>
                      <span className={`shrink-0 inline-flex items-center justify-center w-7 h-7 text-primary transition-all duration-300 ease-out ${isOpen ? "rotate-45 opacity-100" : "rotate-0 opacity-70 group-hover:opacity-100"}`} aria-hidden="true">
                        <Plus className="h-5 w-5" strokeWidth={1.25} />
                      </span>
                    </AccordionTrigger>

                    <AccordionContent className="pb-8 pt-2 pl-0 md:pl-6">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                        <p className="md:col-span-2 font-default text-foreground/85 leading-relaxed" style={{
                      fontSize: "0.9375rem",
                      lineHeight: 1.7
                    }}>
                          {item.answer}
                        </p>

                        {}
                        <div className="hidden md:block relative">
                          
                          
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}

              {}
              <div className="relative border-t border-foreground/20 pt-6 flex items-center gap-2">
                <div className="h-1 w-1 bg-primary" />
                <div className="h-px w-16 bg-primary" />
                <span className="font-mono text-[0.7rem] uppercase tracking-[0.18em] text-foreground/55 ml-2">
                  End of FAQ
                </span>
              </div>
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}